import React, { useState, useMemo } from 'react';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { useAuth } from '@/contexts/AuthContext';
import { Play, X, Lock } from 'lucide-react';

/**
 * Memory Archive (a.k.a. Gallery) — privacy-gated.
 *
 * PUBLIC pages (anyone NOT signed in) see ONLY hand-curated demo
 * memories from `DEMO_MEMORIES` below. These are generated brand
 * imagery — never user uploads, never live database content.
 *
 * AUTHENTICATED users see their real media from FamilyContext
 * exactly as before. No upload logic, no schema, no query, no
 * existing UI structure was changed — this is a UI-level data
 * swap only.
 *
 * NOTE: The /invite route renders InviteLanding, which does not
 * mount this component at all, so invite pages are already safe.
 * The change here protects the public homepage (/) for visitors
 * who have not authenticated.
 */

type DemoMemory = {
  id: string;
  type: 'photo' | 'video';
  url: string;
  date: string;
  caption: string;
  personName: string;
};

// Static, brand-styled placeholder memories shown to logged-out
// visitors only. These are AI-generated demo images — they never
// touch the database and never reference real users.
const DEMO_MEMORIES: DemoMemory[] = [
  {
    id: 'demo-1',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413350982_b18bd243.png',
    date: '2024-07-04',
    caption: 'Summer reunion at the lake house',
    personName: 'The Whitfield Family',
  },
  {
    id: 'demo-2',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413291475_568d0662.jpg',
    date: '1962-09-12',
    caption: 'Grandma & Grandpa, fifty years strong',
    personName: 'Harold & Margaret',
  },
  {
    id: 'demo-3',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413345923_ce601a03.jpg',
    date: '2023-12-24',
    caption: 'Christmas Eve, the whole family together',
    personName: 'The Family',
  },
  {
    id: 'demo-4',
    type: 'video',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413298104_81142032.jpg',
    date: '2010-03-22',
    caption: "Emma's first steps",
    personName: 'Emma',
  },
  {
    id: 'demo-5',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413294394_ce92328a.jpg',
    date: '1948-06-15',
    caption: 'Wedding day at the homestead',
    personName: 'Harold & Margaret',
  },
  {
    id: 'demo-6',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413294132_4a0af1ec.jpg',
    date: '1975-08-10',
    caption: 'A Sunday afternoon, captured forever',
    personName: 'Robert',
  },
  {
    id: 'demo-7',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413319630_7f2f3f1e.png',
    date: '1985-09-12',
    caption: "Michael's first birthday",
    personName: 'Michael',
  },
  {
    id: 'demo-8',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413397312_940e8dea.png',
    date: '2022-07-04',
    caption: 'Independence Day backyard barbecue',
    personName: 'The Whitfields',
  },
  {
    id: 'demo-9',
    type: 'video',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413312444_2f3abcb4.png',
    date: '2005-10-15',
    caption: 'Wedding toast — three generations watching',
    personName: 'Michael & Jennifer',
  },
  {
    id: 'demo-10',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413314159_68449f9b.png',
    date: '1992-06-21',
    caption: 'Cousins at the lake, summer of \'92',
    personName: 'The Cousins',
  },
  {
    id: 'demo-11',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413321903_3bd7c33b.png',
    date: '2015-11-20',
    caption: "Honoring Grandma Margaret's life",
    personName: 'Margaret',
  },
  {
    id: 'demo-12',
    type: 'photo',
    url: 'https://d64gsuwffb70l.cloudfront.net/69e6bb35fd69d67cb9452eee_1777413301987_85f30f22.jpg',
    date: '1955-08-20',
    caption: 'Family farm, August afternoon',
    personName: 'Harold',
  },
];

const Gallery: React.FC = () => {
  const { photos, members } = useFamilyGraph();
  const { isAuthenticated } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);

  // Decide which dataset to render. Public visitors NEVER see live
  // user uploads — they only see DEMO_MEMORIES.
  const items = useMemo(() => {
    if (!isAuthenticated) {
      return DEMO_MEMORIES.map((m) => ({
        id: m.id,
        type: m.type,
        url: m.url,
        date: m.date,
        caption: m.caption,
        personLabel: m.personName,
        isDemo: true as const,
      }));
    }
    const recent = photos
      .map((p) => {
        const member = members.find((candidate) => candidate.id === p.memberId);
        return {
          id: p.id,
          type: p.mediaType === 'video' ? 'video' as const : 'photo' as const,
          url: p.url,
          date: p.eventDate ?? p.createdAt.slice(0, 10),
          caption: p.caption ?? p.title ?? '',
          personLabel: member?.name,
          isDemo: false as const,
        };
      })
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 12);
    return recent;
  }, [isAuthenticated, photos, members]);

  return (
    <section id="gallery" className="py-20 bg-amber-950 text-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block text-xs uppercase tracking-widest text-amber-300 mb-3">
            Memory Archive
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-4">
            {isAuthenticated ? 'Recent Family Memories' : 'A Glimpse of What Families Build'}
          </h2>
          <p className="text-amber-200/80 max-w-2xl mx-auto">
            {isAuthenticated
              ? 'A curated glimpse of the moments that make our family who we are.'
              : 'A sample preview of how memories look inside KinsCircle. Real family photos and videos are private — visible only to verified family members after sign-in.'}
          </p>

          {!isAuthenticated && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-900/60 border border-amber-700/60 text-amber-100 text-xs">
              <Lock className="w-3.5 h-3.5" />
              <span>Sample memories shown — your family's archive is private</span>
            </div>
          )}
        </div>


        {isAuthenticated && (
          <div className="mt-6 mb-6 rounded-2xl border border-amber-700/40 bg-amber-900/30 px-5 py-4 text-center">
            <div className="text-lg font-semibold text-amber-100">✨ Add memories from any profile card</div>
            <p className="mt-2 text-sm text-amber-200/80">
              Tap a family member in your tree, then choose Photo, Video, or Memory.
              Memories automatically appear in your family's archive.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m, i) => (
            <button
              key={m.id}
              onClick={() => setSelected(m.url)}
              className={`group relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-[1.03] ${
                i % 5 === 0
                  ? 'md:row-span-2 md:col-span-2 aspect-square md:aspect-auto'
                  : 'aspect-square'
              }`}
            >
              <img src={m.url} alt={m.caption} className="w-full h-full object-cover" />
              {m.type === 'video' && (
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center">
                  <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
                </div>
              )}
              {m.isDemo && (
                <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-[9px] uppercase tracking-widest text-amber-200 border border-amber-200/30">
                  Sample
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300">
                    {(() => {
                      const y = new Date(m.date).getFullYear();
                      return Number.isFinite(y) ? y : '';
                    })()}
                  </div>
                  <div className="text-sm font-semibold text-white line-clamp-2">{m.caption}</div>
                  {m.personLabel && (
                    <div className="text-xs text-amber-200/80 mt-1">{m.personLabel}</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <img src={selected} alt="" className="max-w-full max-h-full rounded-lg" />
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white"
            onClick={() => setSelected(null)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
